/**
 * ActivityMap — full-canvas map for the OEM Home Dashboard.
 *
 * Two render modes (POC):
 *  - RESTING (plotMode = null): plots REAL individual asset points (clustered),
 *    name-labelled, coloured by fleet category (Ready / Needs Repair / Out of
 *    Service). Consumes `useHomeEntityPoints(range, ASSET)`.
 *  - PLOTTING (plotMode = ASSET | ACTIVITY | ROUTINE | PRODUCT): a card is
 *    selected, so the map plots that entity's individual points, rendered
 *    CLUSTERED (bounded but large for assets) and coloured by status. Consumes
 *    `useHomeEntityPoints(range, plotMode)`. ROUTINE plots assets that have
 *    routine activity, coloured by routine; PRODUCT plots located assets,
 *    coloured by product.
 *
 * `searchQuery` filters the visible markers by name (client-side, case-insensitive).
 *
 * Web-only — do NOT import from mobile code.
 */

import { HomeEntityType, IssueType } from '@gripp/shared-logic';
import { tokens } from '@gripp/shared-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StyleSheet, Text, View } from 'react-native';
import {
  useHomeEntityPoints,
  type HomeMapPoint,
} from '../hooks/useHomeEntityPoints';
import { type HomeAssetTrack } from '../hooks/useHomeAssetTracks';
import { type TimeRange } from '../types';
import { type MapMarker, type MapTrack, MapView } from './mapView';

interface ActivityMapProps {
  range: TimeRange;
  /** Active plotting mode (null = resting view, which plots ASSET points). */
  plotMode?: HomeEntityType | null;
  /**
   * Sub-filter on the point `status` (asset category, activity bucket, routine
   * name, or product name) set by clicking a chart item. null = show all points.
   */
  statusFilter?: string | null;
  /** Case-insensitive name filter applied to the visible markers. */
  searchQuery?: string;
  /**
   * Asset tracks (one polyline per asset) to draw over the basemap, fed through
   * to MapView's optional `tracks` prop. Used by the Pulse resting view; the
   * OEM dashboard does not pass this, so the OEM map is unaffected.
   */
  tracks?: HomeAssetTrack[];
  /**
   * Asset id of the track to HIGHLIGHT (the hovered asset). Threaded to MapView
   * as `highlightedTrackId` (the MapTrack id IS the asset id — see
   * trackToMapTrack), so its polyline + endpoint pops while the others dim.
   */
  highlightedAssetId?: string | null;
  /**
   * Fired when the pointer enters / leaves an individual asset dot (the dot's
   * asset id on enter, null on leave) — threaded straight to MapView. Used by
   * the Pulse resting view to highlight the hovered asset's track.
   */
  onMarkerHover?: (markerId: string | null) => void;
  /**
   * Pulse People card → field-map filter: account id of the selected person.
   * Narrows ASSET-mode points (resting view) to the assets that person has
   * scanned. Honoured server-side only in ASSET mode; harmless in other modes.
   */
  personAccountId?: string | null;
  /**
   * Filter-panel facets (multi), threaded server-side via the home hooks →
   * HomeActivityFilter. `groupIds` narrows the ASSET layer (resting map, tracks,
   * asset/routine/product points) to any of these groups; `labelIds` +
   * `severityBuckets` + `issueTypes` narrow the ACTIVITY layer (issues carrying
   * any label / of any severity bucket / of any type — OR-combined). Empty =
   * inactive.
   */
  groupIds?: string[];
  labelIds?: string[];
  severityBuckets?: string[];
  issueTypes?: IssueType[];
  /**
   * GROUPS / LABELS "(not grouped)" / "(not labeled)" meta-pills, threaded to the
   * same layers as `groupIds` / `labelIds` (groups → ASSET layer; labels → ASSET +
   * ACTIVITY). Booleans carry intent (false = "All"); `undefined` = inactive.
   */
  includeUngrouped?: boolean;
  includeUnlabeled?: boolean;
}

// Severity-derived activity status → Crop Circle semantic colour (matches the
// Activity card's Solved/Pending/Urgent palette).
const ACTIVITY_STATUS_COLORS: Record<string, string> = {
  urgent: tokens.colors.semantic.error,
  pending: tokens.colors.semantic.warning,
  solved: tokens.colors.semantic.success,
};

// Asset fleet category → Crop Circle semantic colour (matches the Assets card
// donut). Keyed by the real status labels the backend now returns.
const ASSET_CATEGORY_COLORS: Record<string, string> = {
  Ready: tokens.colors.semantic.success, // green
  'Needs Repair': tokens.colors.semantic.warning, // yellow
  'Out of Service': tokens.colors.semantic.error, // red
};
// Fallback for any unexpected / missing status label.
const ASSET_CATEGORY_FALLBACK = tokens.colors.slate[400];

// Categorical label → colour. Routines and products aren't good/bad, so use the
// Crop Circle label palette (distinct hues) assigned deterministically by name,
// so a given routine / product keeps the same colour across renders. Shared by
// the ROUTINE (by routine name) and PRODUCT (by product name) plotting modes.
const CATEGORICAL_PALETTE: string[] = [
  tokens.colors.label.indigo,
  tokens.colors.label.teal,
  tokens.colors.label.magenta,
  tokens.colors.label.violet,
  tokens.colors.label.sand,
  tokens.colors.label.cyan,
];
function categoricalColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return CATEGORICAL_PALETTE[Math.abs(hash) % CATEGORICAL_PALETTE.length];
}

/** Converts a plotting-mode `HomeMapPoint` to a coloured `MapMarker`. */
function entityPointToMarker(
  point: HomeMapPoint,
  mode: HomeEntityType
): MapMarker {
  const status = point.status ?? '';
  let color: string = tokens.colors.semantic.success;
  let bucket: MapMarker['bucket'];

  if (mode === HomeEntityType.Activity) {
    color = ACTIVITY_STATUS_COLORS[status] ?? tokens.colors.semantic.success;
    if (status === 'urgent' || status === 'pending' || status === 'solved') {
      bucket = status;
    }
  } else if (mode === HomeEntityType.Asset) {
    color = ASSET_CATEGORY_COLORS[status] ?? ASSET_CATEGORY_FALLBACK;
  } else if (mode === HomeEntityType.Routine) {
    // status is the routine name; colour categorically by routine.
    color = categoricalColor(status);
  } else if (mode === HomeEntityType.Product) {
    // status is the product name; colour categorically by product.
    color = categoricalColor(status);
  }

  return {
    id: point.id,
    name: point.name,
    lat: point.lat,
    lng: point.lng,
    color,
    bucket,
  };
}

/**
 * Converts a `HomeAssetTrack` to a `MapTrack`, colouring the polyline + endpoint
 * marker by the asset's fleet category — the SAME palette as the ASSET markers
 * (entityPointToMarker above), so a track's endpoint dot matches the resting
 * asset dot for that category.
 */
function trackToMapTrack(track: HomeAssetTrack): MapTrack {
  return {
    id: track.assetId,
    name: track.name,
    color: ASSET_CATEGORY_COLORS[track.status] ?? ASSET_CATEGORY_FALLBACK,
    points: track.points.map((p) => ({ lng: p.lng, lat: p.lat })),
  };
}

const DEFAULT_CENTER: [number, number] = [0, 20]; // [lng, lat]
const DEFAULT_ZOOM = 1.5;

export function ActivityMap({
  range,
  plotMode,
  statusFilter,
  searchQuery,
  tracks,
  highlightedAssetId,
  onMarkerHover,
  personAccountId,
  groupIds,
  labelIds,
  severityBuckets,
  issueTypes,
  includeUngrouped,
  includeUnlabeled,
}: ActivityMapProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // RESTING (plotMode null) defaults to real ASSET points so the home map shows
  // the live fleet, coloured by category. PLOTTING uses the selected entity.
  // Either way the map plots individual entity points, clustered.
  const effectiveMode = plotMode ?? HomeEntityType.Asset;
  // The activity layer is ACTIVITY mode; everything else (ASSET/ROUTINE/PRODUCT)
  // is asset-based. Pass each facet only to the layer(s) it applies to so a
  // selection doesn't churn the cache key (or refetch) of the other modes:
  //   - person + groups → asset layer
  //   - severity buckets + issue types → activity layer
  //   - labels → BOTH (gripp-os model): on the ASSET layer they narrow the fleet
  //     to assets owning a matching labelled issue; on the ACTIVITY layer they
  //     filter the issues directly. (ROUTINE/PRODUCT ignore labels server-side.)
  const isActivityLayer = effectiveMode === HomeEntityType.Activity;
  const entityQuery = useHomeEntityPoints(range, effectiveMode, {
    personAccountId: effectiveMode === HomeEntityType.Asset ? personAccountId : null,
    groupIds: !isActivityLayer ? groupIds : undefined,
    labelIds:
      effectiveMode === HomeEntityType.Asset || isActivityLayer
        ? labelIds
        : undefined,
    severityBuckets: isActivityLayer ? severityBuckets : undefined,
    issueTypes: isActivityLayer ? issueTypes : undefined,
    // "(not grouped)" → asset layer (groups); "(not labeled)" → asset + activity.
    includeUngrouped: !isActivityLayer ? includeUngrouped : undefined,
    includeUnlabeled:
      effectiveMode === HomeEntityType.Asset || isActivityLayer
        ? includeUnlabeled
        : undefined,
  });

  // Clicking an individual point opens that entity's detail page. ACTIVITY
  // markers carry an issue id (→ activity detail); ASSET, ROUTINE and PRODUCT
  // markers all carry an asset id (→ asset detail).
  const handleMarkerClick = useCallback(
    (marker: MapMarker) => {
      if (effectiveMode === HomeEntityType.Activity) {
        navigate(`/activities/${marker.id}`);
      } else {
        // ASSET, ROUTINE and PRODUCT markers all carry an asset id → open the asset.
        navigate(`/assets/${marker.id}`);
      }
    },
    [effectiveMode, navigate]
  );

  const isLoading = entityQuery.isLoading;
  const error = entityQuery.error;

  const markers = useMemo(() => {
    let points = entityQuery.data ?? [];
    // Sub-filter from a clicked chart item (matches the point's status:
    // asset category / activity bucket / routine name / product name).
    if (statusFilter) {
      points = points.filter((p) => p.status === statusFilter);
    }
    let base = points.map((p) => entityPointToMarker(p, effectiveMode));

    const q = searchQuery?.trim().toLowerCase();
    if (q) base = base.filter((m) => m.name.toLowerCase().includes(q));
    return base;
  }, [effectiveMode, entityQuery.data, statusFilter, searchQuery]);

  // Asset tracks render only in the resting view (no plotMode) — when a card is
  // selected the map switches to that entity's points and tracks would clutter
  // the plot. Filtered by the same case-insensitive name search as the markers
  // so the two stay in sync.
  // Identity of the current framing context for the map's auto-fit: every
  // deliberate input that changes WHICH markers are shown (range, mode, the
  // server facets, and the client-side status / search filters). The map
  // re-frames when this changes — NOT when `markers` merely gets a new
  // reference from a background refetch, which would otherwise reset a manual
  // zoom (the "streets only show sometimes when I zoom in" bug). Excludes the
  // marker data itself by design.
  const fitKey = useMemo(
    () =>
      JSON.stringify([
        range,
        effectiveMode,
        statusFilter ?? null,
        searchQuery?.trim() ?? '',
        personAccountId ?? null,
        groupIds ?? null,
        labelIds ?? null,
        severityBuckets ?? null,
        issueTypes ?? null,
        includeUngrouped ?? null,
        includeUnlabeled ?? null,
      ]),
    [
      range,
      effectiveMode,
      statusFilter,
      searchQuery,
      personAccountId,
      groupIds,
      labelIds,
      severityBuckets,
      issueTypes,
      includeUngrouped,
      includeUnlabeled,
    ]
  );

  const mapTracks = useMemo<MapTrack[] | undefined>(() => {
    if (plotMode || !tracks) return undefined;
    let base = tracks.map(trackToMapTrack);
    const q = searchQuery?.trim().toLowerCase();
    if (q) base = base.filter((tr) => tr.name.toLowerCase().includes(q));
    return base;
  }, [plotMode, tracks, searchQuery]);

  // The basemap always renders; loading / error / empty are overlays on top.
  let status: { message: string; testID: string } | null = null;
  if (isLoading) {
    status = {
      message: t('home.map.loading', 'Loading map…'),
      testID: 'activity-map-loading',
    };
  } else if (error) {
    status = {
      message: t('home.map.error', 'Unable to load map data.'),
      testID: 'activity-map-error',
    };
  } else if (markers.length === 0) {
    // Empty copy follows the plotted entity (resting defaults to ASSET) so it
    // never reads "No assets" while plotting activities/routines/products.
    const emptyByMode: Record<HomeEntityType, string> = {
      [HomeEntityType.Asset]: t('home.map.emptyAssets', 'No assets for this period.'),
      [HomeEntityType.Activity]: t('home.map.emptyActivity', 'No activity for this period.'),
      [HomeEntityType.Routine]: t('home.map.emptyRoutines', 'No routine activity for this period.'),
      [HomeEntityType.Product]: t('home.map.emptyProducts', 'No products for this period.'),
    };
    status = {
      message: searchQuery?.trim()
        ? t('home.map.noResults', 'No matches for this search.')
        : emptyByMode[effectiveMode] ?? t('home.map.empty', 'No data for this period.'),
      testID: 'activity-map-empty',
    };
  }

  return (
    <View style={styles.fill} testID="activity-map">
      <MapView
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        markers={markers}
        onMarkerClick={handleMarkerClick}
        // Highlight-on-hover is a resting-view concern: when a card is plotting,
        // the map shows that entity's points (no tracks), so we forward neither
        // hover nor highlight in plot mode.
        onMarkerHover={plotMode ? undefined : onMarkerHover}
        highlightedTrackId={plotMode ? null : highlightedAssetId}
        fitToMarkers
        fitKey={fitKey}
        clustered
        tracks={mapTracks}
        // The resting view already plots one labelled ASSET marker per asset
        // (clustered, clickable, searchable) at its lastLocation, so the track
        // layer draws polylines ONLY — no endpoint marker — to avoid a duplicate
        // dot per asset. The asset marker is the single dot; the polyline shows
        // its path and the hover highlight emphasises the line.
        showTrackEndpoints={false}
      />
      {status && (
        <View
          style={styles.statusOverlay}
          pointerEvents="none"
          testID={status.testID}
        >
          <Text style={styles.statusText}>{status.message}</Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  fill: {
    flex: 1,
  },
  statusOverlay: {
    position: 'absolute',
    bottom: tokens.spacing.lg * 5, // above the card strip
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  statusText: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[600],
    backgroundColor: `${tokens.colors.brand.white}D9`, // ~85% opacity
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
    overflow: 'hidden',
  },
});
